import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';

/** Eén bolletje in de gedeelde dot-picker. */
export interface HdDotPickerOption {
  readonly id: string;
  /** CSS-achtergrond van het bolletje (hex of css-kleur). */
  readonly color: string;
  /** Tooltip/aria-tekst. */
  readonly label: string;
}

/**
 * Gedeelde bolletjes-selector (finetune 2026-09-22): één implementatie voor
 * elke rij ronde kleurbolletjes (kleur-presets, stemming, ...). Gedrag:
 * klikken selecteert, nogmaals klikken op de actieve wist de keuze. De
 * selectiestaat is een ring + wit vinkje; de twee-klassen-selector
 * (`.hd-dot-picker__swatch.is-active`) wint bewust van de globale
 * `button:focus`-reset, zodat de ring meteen bij de klik zichtbaar is.
 */
@Component({
  selector: 'hd-dot-picker',
  standalone: true,
  imports: [],
  template: `
    @for (option of options(); track option.id) {
      <button
        type="button"
        class="hd-dot-picker__swatch"
        [class.is-active]="value() === option.id"
        [style.background]="option.color"
        [title]="option.label"
        [attr.aria-label]="option.label"
        [attr.aria-pressed]="value() === option.id"
        (click)="pick(option.id)"
        [attr.data-test-id]="testIdPrefix() + option.id"
      >
        @if (value() === option.id) {
          <i class="fas fa-check" aria-hidden="true"></i>
        }
      </button>
    }
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }

    .hd-dot-picker__swatch {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      padding: 0;
      border: 1px solid var(--separator);
      border-radius: 50%;
      cursor: pointer;
      color: #fff;
      font-size: 0.62rem;

      &.is-active {
        outline: 2px solid var(--dark-middle, #555);
        outline-offset: 2px;
      }

      i {
        text-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdDotPickerComponent {
  readonly options = input.required<readonly HdDotPickerOption[]>();
  /** Geselecteerde optie-id; null = geen keuze. */
  readonly value = model<string | null>(null);
  /** Prefix voor de per-bolletje data-test-id's (bv. "moment-mood-"). */
  readonly testIdPrefix = input('dot-');

  protected pick(id: string): void {
    this.value.set(this.value() === id ? null : id);
  }
}
